import { useGetList } from 'ra-core'
import { usePagination } from 'react-use-pagination'
import { XMarkIcon } from '@heroicons/react/24/outline'
import {
  CheckIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@mui/material'
import { useCustomChartContext } from './CustomChartState'
import { Selectable } from './types'
import { Pagination } from '../Pagination'
import { Button } from '../common/Button'
import { dataTypes, isMultiSeries } from './helpers'

const PAGE_SIZE = 15
const MIN_ITEMS = 1
const MAX_ITEMS = 10

const paramLabels = {
  tags: 'Tags',
  vendors: 'Vendors',
  tag_categories: 'Tag Categories',
}

export const ItemPickerPreview = () => {
  const {
    state: { items, dataType, selectedTagCategory },
    dispatch,
  } = useCustomChartContext()
  const baseItems = useMemo(() => items ?? [], [items])

  const onSelect = useCallback(
    (item: Selectable) => {
      const newItems = baseItems.some((i) => i.id === item.id)
        ? baseItems.filter((i) => i.id !== item.id)
        : [...baseItems, item]
      dispatch({ type: 'SET_ITEMS', payload: { items: newItems } })
    },
    [baseItems, dispatch]
  )

  if (!items || items?.length === 0) return <div />

  return (
    <div className="space-y-2 pl-3 text-indigo-700">
      {isMultiSeries(dataType) && selectedTagCategory ? (
        <div>
          Tag Category:{' '}
          <span className="font-light">{selectedTagCategory.name}</span>
        </div>
      ) : null}
      <div>
        {items.length} item{items.length > 1 ? 's' : ''} selected
      </div>
      <div className="flex flex-wrap gap-x-2.5 gap-y-1.5 items-center">
        {items?.map((item) => (
          <button
            key={item.id}
            onClick={() => onSelect(item)}
            className="border text-indigo-500 hover:bg-white bg-indigo-50/40 shadow-sm text-sm rounded-lg px-2 py-1.5 font-medium flex gap-1.5 items-center active border-gray-300"
            type="button"
            title={item.name}
          >
            <span className="flex-1 line-clamp-2 text-left">{item.name}</span>
            <XMarkIcon className="h-3.5" />
          </button>
        ))}
      </div>
    </div>
  )
}

export const ItemPicker = () => {
  const { t } = useTranslation()
  const {
    state: { dataType, items, selectedTagCategory },
    dispatch,
  } = useCustomChartContext()
  const [openTooltip, setOpenTooltip] = useState(false)
  const [filter, setFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  const paramType = useMemo(
    () => (dataTypes.find((d) => d.id === dataType) ?? dataTypes[0]).params,
    [dataType]
  )

  const { total } = useGetList<Selectable>(paramType, {
    filter: {
      ...(filter ? { q: filter } : {}),
      ...(selectedTagCategory
        ? { tag_category_id: selectedTagCategory.id }
        : {}),
    },
  })

  const { data: tagCategories, isLoading: isLoadingCategories } =
    useGetList<Selectable>('tag_categories', {
      filter: categoryFilter ? { q: categoryFilter } : null,
      sort: { field: 'name', order: 'asc' },
      pagination: { page: 1, perPage: 1000 },
    })

  const {
    setPage,
    setNextPage,
    setPreviousPage,
    nextEnabled,
    previousEnabled,
    startIndex,
    endIndex,
    totalItems,
    currentPage,
  } = usePagination({
    totalItems: total,
    initialPageSize: PAGE_SIZE,
  })

  const { data, isLoading } = useGetList<Selectable>(paramType, {
    filter: {
      ...(filter ? { q: filter } : {}),
      ...(selectedTagCategory
        ? { tag_category_id: selectedTagCategory.id }
        : {}),
    },
    sort: { field: 'name', order: 'asc' },
    pagination: { page: currentPage + 1, perPage: PAGE_SIZE },
  })

  useEffect(() => {
    setPage(0)
  }, [selectedTagCategory, setPage])

  const baseItems = useMemo(() => items ?? [], [items])

  const onSelect = useCallback(
    (item: Selectable) => {
      const newItems = baseItems.some((i) => i.id === item.id)
        ? baseItems.filter((i) => i.id !== item.id)
        : [...baseItems, item]
      dispatch({ type: 'SET_ITEMS', payload: { items: newItems } })
    },
    [baseItems, dispatch]
  )

  const onSelectCategory = useCallback(
    (category: Selectable | null) => {
      dispatch({ type: 'SET_TAG_CATEGORY', payload: { tagCategory: category } })
    },
    [dispatch]
  )

  const canContinue = useMemo(
    () => baseItems.length >= MIN_ITEMS && baseItems.length <= MAX_ITEMS,
    [baseItems]
  )

  const handleTooltipClose = () => {
    setOpenTooltip(false)
  }

  const handleTooltipOpen = () => {
    if (!canContinue) {
      setOpenTooltip(true)
    }
  }

  const onContinue = useCallback(() => {
    dispatch({ type: 'SET_CURRENT', payload: { current: 2 } })
    setFilter('')
  }, [dispatch])

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col h-[calc(100%-50px)]">
        <div className="flex flex-col h-full space-y-4 overflow-hidden">
          {isMultiSeries(dataType) && (
            <div className="mb-3 h-[35%] max-h-[400px]">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-lg">
                  {paramLabels.tag_categories}
                </h3>
                <div className="relative w-1/4">
                  <input
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    placeholder={t('custom_widgets.filter_categories')}
                    type="text"
                    className="py-2 px-4 border border-gray-300 rounded-full shadow-inner w-full text-sm"
                  />
                  <div className="absolute right-3 top-0 bottom-0 flex items-center">
                    {categoryFilter ? (
                      <XMarkIcon
                        className="h-4 text-gray-800 cursor-pointer active"
                        onClick={() => setCategoryFilter('')}
                      />
                    ) : (
                      <MagnifyingGlassIcon className="h-4 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
              <div className="text-sm bg-white rounded-lg border border-gray-300 shadow-sm divide-y overflow-y-auto h-[calc(100%-32px)]">
                {isLoadingCategories ? (
                  <div className="w-full h-24 flex items-center justify-center text-gray-600 animate-pulse">
                    <span>Loading categories...</span>
                  </div>
                ) : tagCategories && tagCategories.length > 0 ? (
                  tagCategories.map((category) => {
                    const isSelected = selectedTagCategory?.id === category.id
                    return (
                      <div
                        key={category.id}
                        className={classNames(
                          'px-4 py-2 active hover:bg-gray-50 cursor-pointer flex items-center justify-between text-sm',
                          {
                            'text-indigo-700 font-medium bg-indigo-50/50':
                              isSelected,
                          }
                        )}
                        onClick={() =>
                          onSelectCategory(isSelected ? null : category)
                        }
                      >
                        <span className="flex-1">{category.name}</span>
                        {isSelected && (
                          <CheckIcon className="h-4 text-indigo-700" />
                        )}
                      </div>
                    )
                  })
                ) : (
                  <div className="flex items-center justify-center w-full h-24">
                    No tag categories found
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="h-[65%] max-h-[calc(100%-250px)] flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-lg">{paramLabels[paramType]}</h3>
              <div className="relative w-1/4">
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={t('custom_widgets.filter')}
                  type="text"
                  className="py-2 px-4 border border-gray-300 rounded-full shadow-inner w-full text-sm"
                />
                <div className="absolute right-3 top-0 bottom-0 flex items-center">
                  {filter ? (
                    <XMarkIcon
                      className="h-4 text-gray-800 cursor-pointer active"
                      onClick={() => setFilter('')}
                    />
                  ) : (
                    <MagnifyingGlassIcon className="h-4 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            <div className="text-sm bg-white rounded-lg border border-gray-300 shadow-sm divide-y overflow-y-auto h-[calc(100%-60px)]">
              {data && data.length === 0 && (
                <div className="flex items-center justify-center w-full h-24">
                  {selectedTagCategory
                    ? 'No tags in this category'
                    : 'There are no items'}
                </div>
              )}
              {isLoading ? (
                <div className="w-full h-24 flex items-center justify-center text-gray-600 animate-pulse">
                  <span>Loading...</span>
                </div>
              ) : (
                data?.map((d) => {
                  const isSelected = baseItems.some((i) => i.id === d.id)
                  return (
                    <div
                      key={d.id}
                      className={classNames(
                        'px-4 py-2 active hover:bg-gray-50 cursor-pointer flex items-center justify-between text-sm',
                        {
                          'text-indigo-700 font-medium bg-indigo-50/50':
                            isSelected,
                        }
                      )}
                      onClick={() => onSelect(d)}
                    >
                      <span className="flex-1">{d.name}</span>
                      {isSelected && (
                        <CheckIcon className="h-4 text-indigo-700" />
                      )}
                    </div>
                  )
                })
              )}
            </div>

            <div className="mt-2">
              <Pagination
                goToNextPage={setNextPage}
                goToPreviousPage={setPreviousPage}
                canPreviousPage={previousEnabled}
                canNextPage={nextEnabled}
                startIndex={startIndex}
                endIndex={endIndex}
                totalItems={totalItems}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-2">
        <Button
          onClick={() =>
            dispatch({ type: 'SET_CURRENT', payload: { current: 0 } })
          }
          large
          secondary
        >
          <span className="flex items-center gap-2">
            <ChevronRightIcon className="h-3 rotate-180" />
            <span>{t('custom_widgets.back')}</span>
          </span>
        </Button>
        <Tooltip
          title={`You cannot select ore than ${MAX_ITEMS} items.`}
          placement="top-end"
          open={openTooltip}
          onClose={handleTooltipClose}
          onOpen={handleTooltipOpen}
        >
          <div className="relative group">
            <Button disabled={!canContinue} onClick={onContinue} large>
              <span className="flex items-center gap-2">
                <span>{t('custom_widgets.continue')}</span>
                <ChevronRightIcon className="h-3" />
              </span>
            </Button>
          </div>
        </Tooltip>
      </div>
    </div>
  )
}
